import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Eye, Heart, Coffee, MessageCircle, Mail } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("ro-RO", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
};

const SummaryCard = ({ icon: Icon, label, value, bg, accent, testId }) => (
  <div data-testid={testId} className={`rounded-2xl ${bg} px-5 py-4 ring-1 ring-[color-mix(in_srgb,var(--milk)_18%,transparent)]`}>
    <div className="flex items-center gap-2">
      <Icon className={`h-4 w-4 ${accent}`} strokeWidth={1.6} />
      <p className="text-[11px] uppercase tracking-[0.2em] text-[#F7F1E8]/78">{label}</p>
    </div>
    <p className="mt-2 font-serif text-3xl text-[#F7F1E8]">{value}</p>
  </div>
);

export const AdminStatsPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/admin/stats")
      .then((r) => setStats(r.data))
      .catch((e) => toast.error(formatApiError(e, "Nu am putut încărca statisticile")))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main data-testid="admin-stats-page" className="mx-auto max-w-6xl px-6 py-12">
      <Link
        to="/admin"
        data-testid="stats-back-link"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78 hover:text-[#F7F1E8]"
      >
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.6} /> Înapoi la Atelier
      </Link>
      <h1 className="mt-6 font-serif text-4xl font-light text-[#F7F1E8]">Statistici</h1>
      <p className="mt-2 text-sm text-[#F7F1E8]/78">
        Vizitele se numără din pagina fiecărei însemnări, fără cookie-uri și fără urmărirea cititorilor.
      </p>

      {loading && (
        <p className="py-16 text-center text-sm text-[#F7F1E8]/68" data-testid="stats-loading">
          Adun cifrele&hellip;
        </p>
      )}

      {!loading && stats && (
        <>
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            <SummaryCard icon={Eye} label="Vizite" value={stats.totals.visits} bg="bg-[var(--tint-mint)]" accent="text-[var(--teal)]" testId="stats-total-visits" />
            <SummaryCard icon={Heart} label="Inimi" value={stats.totals.hearts} bg="bg-[var(--tint-pink)]" accent="text-[var(--accent)]" testId="stats-total-hearts" />
            <SummaryCard icon={Coffee} label="Cafele" value={stats.totals.coffees} bg="bg-[var(--tint-butter)]" accent="text-[var(--sun-dark)]" testId="stats-total-coffees" />
            <SummaryCard icon={MessageCircle} label="Note" value={stats.totals.comments} bg="bg-[var(--tint-peach)]" accent="text-[var(--coral)]" testId="stats-total-comments" />
            <SummaryCard icon={Mail} label="Abonați" value={stats.totals.subscribers} bg="bg-[var(--tint-mint)]" accent="text-[var(--teal-dark)]" testId="stats-total-subscribers" />
          </div>

          {stats.daily && (
            <div className="mt-10 rounded-2xl bg-[#2B1B13] p-6 ring-1 ring-[color-mix(in_srgb,var(--milk)_18%,transparent)]" data-testid="stats-daily-chart">
              <p className="text-xs uppercase tracking-[0.25em] text-[var(--teal)]">Vizite pe zile</p>
              <p className="mt-1 text-[11px] text-[#F7F1E8]/68">Ultimele 30 de zile, toate însemnările cumulate.</p>
              <div className="mt-4 h-56 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={stats.daily} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                    <defs>
                      <linearGradient id="visitsFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#6B4A32" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#6B4A32" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#14100D" strokeOpacity={0.08} vertical={false} />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10, fill: "#F7F1E899" }}
                      tickFormatter={(d) => {
                        const dt = new Date(d + "T00:00:00");
                        return dt.toLocaleDateString("ro-RO", { day: "numeric", month: "short" });
                      }}
                      interval="preserveStartEnd"
                      minTickGap={28}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#F7F1E899" }} axisLine={false} tickLine={false} />
                    <Tooltip
                      formatter={(v) => [`${v} ${v === 1 ? "vizită" : "vizite"}`, null]}
                      labelFormatter={(d) => new Date(d + "T00:00:00").toLocaleDateString("ro-RO", { weekday: "long", day: "numeric", month: "long" })}
                      contentStyle={{ borderRadius: 12, border: "1px solid rgba(5,52,59,0.15)", fontSize: 12, fontFamily: "Outfit, sans-serif" }}
                    />
                    <Area type="monotone" dataKey="visits" stroke="#6B4A32" strokeWidth={2} fill="url(#visitsFill)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div className="mt-10 overflow-x-auto rounded-2xl ring-1 ring-[color-mix(in_srgb,var(--milk)_18%,transparent)]">
            <table className="w-full min-w-[640px] text-left text-sm" data-testid="stats-table">
              <thead>
                <tr className="bg-[var(--accent)] text-[11px] uppercase tracking-[0.18em] text-white/90">
                  <th className="px-5 py-3 font-medium">Însemnare</th>
                  <th className="px-4 py-3 text-center font-medium">Vizite</th>
                  <th className="px-4 py-3 text-center font-medium">Inimi</th>
                  <th className="px-4 py-3 text-center font-medium">Cafele</th>
                  <th className="px-4 py-3 text-center font-medium">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13]">
                {stats.posts.map((p) => (
                  <tr key={p.id} data-testid={`stats-row-${p.slug}`}>
                    <td className="px-5 py-4">
                      <p className="font-serif text-base text-[#F7F1E8]">
                        {p.title}
                        {!p.published && (
                          <span className="ml-2 rounded-full bg-[var(--tint-butter)] px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] text-[var(--sun-dark)]">
                            ciornă
                          </span>
                        )}
                      </p>
                      <p className="mt-0.5 text-[11px] uppercase tracking-[0.15em] text-[#F7F1E8]/58">{fmt(p.created_at)}</p>
                    </td>
                    <td className="px-4 py-4 text-center font-medium text-[var(--teal)]" data-testid={`stats-visits-${p.slug}`}>{p.visits}</td>
                    <td className="px-4 py-4 text-center font-medium text-[var(--accent)]">{p.hearts}</td>
                    <td className="px-4 py-4 text-center font-medium text-[var(--sun-dark)]">{p.coffees}</td>
                    <td className="px-4 py-4 text-center">
                      <span className="font-medium text-[var(--coral)]">{p.comments_approved}</span>
                      {p.comments_pending > 0 && (
                        <span className="ml-1 text-[11px] text-[#F7F1E8]/68">(+{p.comments_pending} în așteptare)</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-4 text-[11px] text-[#F7F1E8]/68">
            Vizitele tale din contul de administrator nu sunt numărate. Contorizarea a început odată cu activarea acestei pagini.
          </p>
        </>
      )}
    </main>
  );
};

export default AdminStatsPage;
